import React, { FC, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";

import { routes } from "@app/routing";
import { PostBillAPIContext, PostBillContext } from "@app/provider";
import { SpinnerView } from "@app/components";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { PaymentPlan, PaymentWorkflow } from "@app/postBill/models";
import {
  getFormattedMonthlyPaymentDate,
  getLimsClinicIds,
  getTestsOrdered,
} from "@app/postBill/utils";
import PaymentSummaryCard from "../paymentSummaryCard";
import PaymentFullSummaryCard from "../paymentFullSummaryCard";
import { usePostBillHeapEvent } from "@app/postBill/hooks";
import { HEAP_EVENTS } from "@app/provider/types";
import { getWalletType } from "@app/postBill/utils/heapHelper";
import { logMsg } from "@app/utils/logMsg";

import "./billingPaymentForm.scss";

const messages = defineMessages({
  billingPaymentPayButton: {
    id: "billingPaymentPayButton",
    defaultMessage: "Pay now",
  },
  billingPaymentDynamicMonthlyPaymentDate: {
    id: "billingPaymentDynamicMonthlyPaymentDate",
    defaultMessage: "{day} of each month",
  },
});

interface BillingPaymentFormProps {
  paymentPlanId?: string;
}

const BillingPaymentForm: FC<BillingPaymentFormProps> = ({ paymentPlanId }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    apiProviderState: {
      paymentSetupData,
      paymentPlanSetupData,
      payFullBalanceData,
    },
    confirmPayment,
    confirmPaymentPlan,
    confirmPayFullBalance,
  } = useContext(PostBillAPIContext);
  const {
    selectedBills,
    totalAmount,
    selectedPaymentPlan,
    selectedPaymentPlanItems,
    setPaymentCompleted,
    paidDirectPayments,
    setPaidDirectPayments,
    paidPaymentPlans,
    setPaidPaymentPlans,
  } = useContext(PostBillContext);
  const [loading, setLoading] = useState<boolean>(false);
  const { trackPostBillHeapEvent } = usePostBillHeapEvent();

  const location = useLocation();
  const { payFullBalance } = location.state ?? {};

  const stripe = useStripe();
  const elements = useElements();

  const updatePaidItems = (paidItems: Set<string>): Set<string> => {
    const newHidden = new Set(paidItems);
    selectedBills.forEach((bill) => newHidden.add(bill?.lineItemUid));
    return newHidden;
  };

  const handleConfirmPayment = async (paymentIntentId: string) => {
    try {
      await confirmPayment({
        paymentRequestId: paymentSetupData?.paymentRequestId ?? "",
        paymentIntentId: paymentIntentId,
      });
    } catch {
      logMsg("error", "postbill.payment", "Error sending payment choice");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPaymentPlan = async (
    paymentMethodId: string,
    setupIntentId: string
  ) => {
    try {
      await confirmPaymentPlan({
        paymentRequestId: paymentPlanSetupData?.paymentRequestId ?? "",
        paymentMethodId: paymentMethodId,
        setupIntentId: setupIntentId,
      });
      setPaymentCompleted(true);

      //Update paid item list with paid billing items with payment plan
      const paidItems = updatePaidItems(paidPaymentPlans);
      setPaidPaymentPlans(paidItems);

      navigate(routes.billingConfirmation, {
        state: {
          action: "payment_plan",
        },
      });
    } catch {
      logMsg("error", "postbill.payment", "Error sending payment plan request");
      navigate(routes.billingFailure);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayFullBalance = async (paymentIntentId: string) => {
    try {
      await confirmPayFullBalance({
        paymentPlanId: paymentPlanId ?? "",
        paymentRequestId: payFullBalanceData?.paymentRequestId ?? "",
        paymentIntentId: paymentIntentId,
      });
      setPaymentCompleted(true);
      navigate(routes.billingConfirmation, {
        state: {
          action: "pay_full_balance",
        },
      });
    } catch {
      logMsg("error", "postbill.payment", "Error on handlePayFullBalance");
      navigate(routes.billingFailure);
    }
  };

  const handlePayFullBalance = async () => {
    if (!stripe || !elements) return;

    trackPostBillHeapEvent({
      eventKey: HEAP_EVENTS.upp_click_billing_payment_paynow,
      extraData: {
        tests_ordered: getTestsOrdered(selectedBills),
        lims_clinic_id: getLimsClinicIds(selectedBills),
        pay_plan_length: selectedPaymentPlan,
      },
    });

    setLoading(true);
    try {
      const { paymentIntent, error } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });
      if (error) {
        trackPostBillHeapEvent({
          eventKey: HEAP_EVENTS.upp_billing_payment_failedpayment,
          extraData: {
            workflow: PaymentWorkflow.PAYMENT_FLOW,
            failure_reason: error.decline_code,
            wallet_type: getWalletType(error.payment_method),
          },
        });

        navigate(routes.billingFailure);
      } else {
        await handleConfirmPayFullBalance(paymentIntent.id);
      }
    } catch {
      logMsg("error", "postbill.payment", "Error sending payment request");
      navigate(routes.billingFailure);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentRequest = async () => {
    const isDefaultPlan = selectedPaymentPlan === PaymentPlan.None;

    const isReady =
      stripe &&
      elements &&
      (isDefaultPlan
        ? paymentSetupData !== undefined
        : paymentPlanSetupData !== undefined);

    if (!isReady) return;

    setLoading(true);

    try {
      if (isDefaultPlan) {
        const { paymentIntent, error } = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });

        trackPostBillHeapEvent({
          eventKey: HEAP_EVENTS.upp_click_billing_payment_paynow,
          extraData: {
            tests_ordered: getTestsOrdered(selectedBills),
            lims_clinic_id: getLimsClinicIds(selectedBills),
            pay_plan_length: selectedPaymentPlan,
            payment_method: paymentIntent?.payment_method_types[0],
          },
        });
        if (error) {
          trackPostBillHeapEvent({
            eventKey: HEAP_EVENTS.upp_billing_payment_failedpayment,
            extraData: {
              workflow: PaymentWorkflow.PAYMENT_FLOW,
              failure_reason: error.decline_code,
              wallet_type: getWalletType(error.payment_method),
            },
          });
          navigate(routes.billingFailure);
        } else {
          await handleConfirmPayment(paymentIntent.id);
          setPaymentCompleted(true);

          //Update paid item list with paid billing items with direct pay
          const paidItems = updatePaidItems(paidDirectPayments);
          setPaidDirectPayments(paidItems);

          navigate(routes.billingConfirmation, {
            state: {
              action: "payment",
            },
          });
        }
      } else {
        const { setupIntent, error } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
        });

        if (error) {
          trackPostBillHeapEvent({
            eventKey: HEAP_EVENTS.upp_billing_payment_failedpayment,
            extraData: {
              workflow: PaymentWorkflow.PAYMENT_PLAN,
              failure_reason: error.decline_code,
              wallet_type: getWalletType(error.payment_method),
            },
          });
          navigate(routes.billingFailure);
        } else {
          handleConfirmPaymentPlan(
            setupIntent.payment_method as string,
            setupIntent.id
          );
        }
      }
    } catch {
      logMsg("error", "postbill.payment", "Error sending payment request");
      navigate(routes.billingFailure);
    }
  };

  const getFirstPaymentDate = (): string => {
    return intl.formatMessage(
      messages.billingPaymentDynamicMonthlyPaymentDate,
      {
        day: getFormattedMonthlyPaymentDate(intl, new Date()),
      }
    );
  };

  const renderPaymentSummaryCard = () => {
    if (payFullBalance && paymentPlanId) {
      return <PaymentFullSummaryCard paymentPlanId={paymentPlanId} />;
    }
    return (
      selectedBills.length > 0 && (
        <PaymentSummaryCard
          selectedPlan={selectedPaymentPlan}
          testNames={selectedBills.map((bill) => bill.testName.shortName)}
          testPrices={selectedBills.map((bill) => bill.balance)}
          totalPrice={totalAmount}
          currency={selectedBills[0]?.currency}
          firstPaymentDate={getFirstPaymentDate()}
          paymentItems={selectedPaymentPlanItems}
        />
      )
    );
  };

  return (
    <>
      <SpinnerView isLoading={loading} />
      <div className="payment-form__container">
        <PaymentElement />
        {renderPaymentSummaryCard()}
        <ButtonNoShadow
          raised
          className="billing-payment__button"
          onClick={paymentPlanId ? handlePayFullBalance : handlePaymentRequest}
        >
          {intl.formatMessage(messages.billingPaymentPayButton)}
        </ButtonNoShadow>
      </div>
    </>
  );
};

export default BillingPaymentForm;
